import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Camera, X, Loader2 } from 'lucide-react';

const MAX_PHOTOS = 5;
const MAX_SIZE_MB = 10;

export default function CustomerPhotoUpload({ photos, onAdd, onRemove, uploading }) {
  const onDrop = useCallback((accepted) => {
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) return;
    accepted.slice(0, remaining).forEach(file => onAdd(file));
  }, [photos.length, onAdd]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    maxSize: MAX_SIZE_MB * 1024 * 1024,
    multiple: true,
    disabled: photos.length >= MAX_PHOTOS || uploading,
  });

  return (
    <div className="mt-4">
      <p className="text-sm font-semibold text-gray-700 mb-2">
        Add Photos <span className="text-gray-400 font-normal">(Optional — up to {MAX_PHOTOS})</span>
      </p>
      <p className="text-xs text-gray-400 mb-3">
        Show us problem areas, stains, or anything you want us to pay special attention to.
      </p>

      {/* Thumbnail grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100">
              <img src={p.preview} alt="" className="w-full h-full object-cover" />
              {p.uploading ? (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <Loader2 size={16} className="text-white animate-spin" />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 hover:bg-black/80 transition-colors"
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drop zone */}
      {photos.length < MAX_PHOTOS && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-[#68caf1] bg-[#68caf1]/5'
              : 'border-gray-200 hover:border-[#68caf1] hover:bg-gray-50'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />
          <Camera size={20} className="mx-auto text-gray-400 mb-1" />
          <p className="text-xs text-gray-500">
            {isDragActive ? 'Drop photos here…' : 'Tap to add photos or drag & drop'}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{photos.length}/{MAX_PHOTOS} · JPG, PNG, HEIC up to {MAX_SIZE_MB}MB each</p>
        </div>
      )}
    </div>
  );
}
